// src/components/Scrutinize/Scrutinize.js
// SCRUTINIZE Scan Page - Terminal Theme

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import scanService from '../../services/scanService';
import './Scrutinize.css';

// Progress messages for the scanning phase
const PROGRESS_MESSAGES = [
  'Initiating comprehensive reconnaissance...',
  'Running full port discovery...',
  'Analyzing SSL/TLS configurations...',
  'Enumerating subdomains and DNS records...',
  'Scanning for known vulnerabilities (CVEs)...',
  'Fingerprinting web technologies...',
  'Testing for web application vulnerabilities...',
  'Mapping attack surface vectors...',
  'Correlating threat intelligence data...',
  'Generating MITRE ATT&CK mappings...',
  'Building penetration testing roadmap...',
  'Running compliance framework assessment...',
  'Synthesizing AI-powered analysis...'
];

// Attack surface coverage areas
const COVERAGE_AREAS = [
  { icon: '🔌', name: 'TCP & UDP Ports' },
  { icon: '💥', name: 'Vuln Checks' },
  { icon: '🌐', name: 'HTTP Analysis' },
  { icon: '🔗', name: 'Link Discovery' },
  { icon: '🗺️', name: 'Subdomains' },
  { icon: '🛡️', name: 'WAF Detection' },
  { icon: '🔧', name: 'Tech Stack' },
  { icon: '📡', name: 'Banner Grabbing' },
  { icon: '🔐', name: 'SSL/TLS Certs' },
  { icon: '📋', name: 'DNS Records' },
  { icon: '🎯', name: 'Service Probing' },
  { icon: '🕵️', name: 'Header Analysis' }
];

// AI Features list
const AI_FEATURES = [
  'MITRE ATT&CK Technique Mapping',
  'Penetration Testing Roadmap (4 Phases)',
  'Threat Intelligence Integration',
  'Prioritized Attack Vectors',
  'Defensive Mitigations',
  'Executive Summary Generation'
];

function Scrutinize() {
  const [target, setTarget] = useState('scanme.nmap.org');
  const [scanning, setScanning] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let progressInterval;
    let messageIndex = 0;

    if (scanning && scanId) {
      // Update progress and status message
      progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 0.5, 95));
        setStatusMessage(PROGRESS_MESSAGES[messageIndex % PROGRESS_MESSAGES.length]);
        messageIndex++;
      }, 5000);

      // Poll for results every 60 seconds
      const pollInterval = setInterval(async () => {
        try {
          // Use getScrutinizeResult for SCRUTINIZE scans (not getScanResult which is for Fast Scans)
          const result = await scanService.getScrutinizeResult(scanId);
          if (result && result.status === 'complete') {
            setProgress(100);
            setStatusMessage('Analysis complete! Redirecting to results...');
            clearInterval(progressInterval);
            clearInterval(pollInterval);

            setTimeout(() => {
              navigate(`/scrutinize-result/${scanId}`);
            }, 2000);
          }
        } catch (err) {
          console.log('Scan still processing:', err.message);
        }
      }, 60000);

      return () => {
        clearInterval(progressInterval);
        clearInterval(pollInterval);
      };
    }
  }, [scanning, scanId, navigate]);

  const validateTarget = (value) => {
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    return domainRegex.test(value) || ipRegex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!target) {
      setError('Please enter a target domain or IP address');
      return;
    }

    if (!validateTarget(target)) {
      setError('Invalid target format. Use domain.com or IP address');
      return;
    }

    try {
      setScanning(true);
      setProgress(5);
      setStatusMessage('Initializing Deep Scan...');

      const response = await scanService.startScrutinizeScan(target);

      if (response.scanId) {
        setScanId(response.scanId);
        setStatusMessage('Scan initiated successfully. Running comprehensive analysis...');
        setProgress(10);
      } else {
        throw new Error('No scan ID received from server');
      }
    } catch (err) {
      setError(`Failed to start scan: ${err.message}`);
      setScanning(false);
      setProgress(0);
      setStatusMessage('');
      console.error('Scan error:', err);
    }
  };

  // Render the input form view
  const renderInputView = () => (
    <div className="scrutinize-content">
      <div className="scrutinize-main-card">
        {/* Terminal Header - Page Title */}
        <div className="terminal-header">
          <span className="terminal-icon">🔬</span>
          <span className="terminal-title">DEEP SCAN</span>
        </div>

        {/* Card Body */}
        <div className="card-body">
          <p className="card-description">
            Deep Scan performs an exhaustive security assessment using 8 specialized 
            reconnaissance tools running in parallel, followed by advanced AI analysis 
            with MITRE ATT&CK mapping, penetration testing roadmaps, and defensive mitigations.
          </p>

          {/* FORM MOVED HERE - Above Tools Arsenal */}
          <form onSubmit={handleSubmit} className="scan-form">
            <div className="input-group">
              <label htmlFor="target" className="input-label">
                Target Domain or IP Address
              </label>
              <input
                type="text"
                id="target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="example.com or 192.168.1.1"
                className="target-input"
                disabled={scanning}
                autoComplete="off"
                spellCheck="false"
              />
              <p className="input-hint">
                Default: scanme.nmap.org (safe test site)
              </p>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={scanning || !target}
            >
              🔬 Initiate Deep Scan
            </button>
          </form>

          {/* Warning */}
          <div className="warning-box">
            <span className="warning-icon">⚠️</span>
            <p className="warning-text">
              Only scan targets you own or have explicit written permission to test. 
              Unauthorized scanning may violate computer crime laws and regulations.
            </p>
          </div>

          {/* Coverage Grid - Now BELOW form */}
          <div className="tools-section">
            <div className="section-label">Attack Surface Coverage</div>
            <div className="tools-grid">
              {COVERAGE_AREAS.map((area) => (
                <div key={area.name} className="tool-badge">
                  <span className="tool-icon">{area.icon}</span>
                  <span className="tool-name">{area.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AI Features */}
          <div className="ai-features">
            <h3 className="features-title">AI-Enhanced Analysis</h3>
            <ul className="features-list">
              {AI_FEATURES.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="scrutinize-info-panel">
        <div className="info-section">
          <h3 className="info-title">What You Get</h3>
          <ul className="info-list">
            <li>Full port discovery & service identification</li>
            <li>Vulnerability assessment with CVE details</li>
            <li>Subdomain enumeration</li>
            <li>SSL/TLS security analysis</li>
            <li>Web technology fingerprinting</li>
            <li>DNS record enumeration</li>
            <li>Web application vulnerability scan</li>
            <li>Attacker-perspective analysis</li>
          </ul>
        </div>

        <div className="info-section">
          <h3 className="info-title">Timing</h3>
          <p className="info-text">
            Comprehensive scans typically complete in <strong>15-20 minutes</strong>.
            You can close this page — results will be saved and accessible from 
            the Reports page.
          </p>
        </div>

        <div className="info-section">
          <h3 className="info-title">Use Cases</h3>
          <ul className="info-list">
            <li>Pre-deployment security assessment</li>
            <li>Penetration testing preparation</li>
            <li>Compliance validation</li>
            <li>Attack surface analysis</li>
            <li>Vulnerability management</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Render the scanning status view
  const renderScanningView = () => (
    <div className="scanning-status">
      <div className="scanning-card">
        {/* Terminal Header */}
        <div className="scanning-card-header">
          <span className="terminal-icon">🔬</span>
          <span className="terminal-title">scrutinize.exe — running</span>
        </div>

        <div className="scanning-card-body">
          <div className="scanning-header">
            <div className="status-indicator">
              <span className="status-dot active"></span>
              <span className="status-text">Scanning in Progress</span>
            </div>
          </div>

          {/* Scan Info */}
          <div className="scan-info">
            <div className="info-row">
              <span className="info-label">Target</span>
              <span className="info-value">{target}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Scan ID</span>
              <span className="info-value mono">{scanId}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status</span>
              <span className="info-value">{statusMessage}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="progress-text">{Math.round(progress)}%</div>
          </div>

          {/* Radar Animation */}
          <div className="scanning-animation">
            <div className="radar-ring"></div>
            <div className="radar-ring delay-1"></div>
            <div className="radar-ring delay-2"></div>
            <div className="radar-sweep"></div>
            <div className="radar-center"></div>
          </div>

          {/* Note */}
          <div className="scanning-note">
            <p>
              ⏱️ Comprehensive scan will take approximately <strong>15-20 minutes</strong>
            </p>
            <p>
              You can safely close this page. Results will be saved and accessible
              from the <strong>Reports</strong> page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="scrutinize-container">
      {!scanning ? renderInputView() : renderScanningView()}
    </div>
  );
}

export default Scrutinize;